/**
 * API Service - Axios instance with authentication and timeout handling
 *
 * Provides configured axios instance for all API calls with:
 * - Automatic Firebase token attachment for authenticated users
 * - Guest ID tracking for unauthenticated users
 * - 60-second timeout for all requests
 * - Timeout error handling with user-friendly messages
 */
import axios from 'axios';
import { auth, FIREBASE_ENABLED } from '../contexts/AuthContext';

const API_BASE = "/api";

/**
 * Configured axios instance for API calls
 * @type {import('axios').AxiosInstance}
 */
const api = axios.create({
  baseURL: API_BASE,
  timeout: 95000, // 95s timeout for all requests (OCR cascade can take up to 55s + matrix calc)
});

// Axios Interceptor to auto-attach Firebase Token, Admin Token, or Guest ID
// Uses getIdToken(true) to force refresh when token is near expiry
api.interceptors.request.use(async (config) => {
  // Admin endpoints require special admin token
  if (config.url?.startsWith('/admin/')) {
    const adminToken = import.meta.env.VITE_ADMIN_TOKEN;
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }
  } else if (FIREBASE_ENABLED && auth?.currentUser) {
    // Check if token needs refresh (within 5 minutes of expiry)
    let forceRefresh = false;
    try {
      const tokenResult = await auth.currentUser.getIdTokenResult();
      const expirationMs = new Date(tokenResult.expirationTime).getTime();
      const fiveMinutes = 5 * 60 * 1000;
      if (expirationMs - Date.now() < fiveMinutes) {
        forceRefresh = true;
      }
    } catch {
      // If we can't check expiry, let getIdToken handle it
    }
    const token = await auth.currentUser.getIdToken(forceRefresh);
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Guest mode - send unique guest ID header
    const guestId = localStorage.getItem('guestId') || `guest_${Math.random().toString(36).substr(2, 12)}`;
    if (!localStorage.getItem('guestId')) {
      localStorage.setItem('guestId', guestId);
    }
    config.headers['X-Guest-ID'] = guestId;
  }
  return config;
});

// Response interceptor for timeout handling and 401 re-auth
api.interceptors.response.use(
  response => response,
  async (error) => {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return Promise.reject(new Error('Request timed out after 60 seconds. Please try again or check your connection.'));
    }

    // 401 Unauthorized — token likely expired, force refresh and retry once
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && auth?.currentUser) {
      originalRequest._retry = true;
      try {
        const newToken = await auth.currentUser.getIdToken(true);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        // Token refresh failed — user needs to re-authenticate
        console.warn('[Auth] Token refresh failed, user may need to sign in again');
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
      }
    }

    return Promise.reject(error);
  }
);

export const BagService = {
  getBag: async () => {
    const res = await api.get('/bag');
    return res.data.items || [];
  },
  getGaps: async () => {
    const res = await api.get('/bag/gaps');
    return res.data;
  },
  addClub: async (club) => {
    const res = await api.post('/bag', { 
      club_name: club.name, 
      category: club.category, 
      carry_distance: parseInt(club.carry) 
    });
    return res.data;
  },
  deleteClub: async (id) => {
    const res = await api.delete(`/bag/${id}`);
    return res.data;
  },
  updateClub: async (id, updates) => {
    const res = await api.patch(`/bag/${id}`, updates);
    return res.data;
  },
  clearBag: async () => {
    const res = await api.delete('/bag');
    return res.data;
  },
  importBag: async (formData) => {
    const res = await api.post('/bag/import', formData);
    return res.data; // Returns { detected_clubs: [...] }
  },
  confirmImport: async (clubsToImport) => {
    const res = await api.post('/bag/import/confirm', clubsToImport);
    return res.data;
  },
  optimizeClub: async (clubId) => {
    const res = await api.post(`/bag/optimize/${clubId}`);
    return res.data;
  },
  // Golf ball management
  getBalls: async () => {
    const res = await api.get('/bag/balls');
    return res.data;
  },
  addBall: async (ballId) => {
    const res = await api.post('/bag/balls', { ball_id: ballId });
    return res.data;
  },
  removeBall: async (id) => {
    const res = await api.delete(`/bag/balls/${id}`);
    return res.data;
  }
};

export const InventoryService = {
  getHeads: async (category = '') => {
    const res = await api.get(`/inventory/heads${category ? `?category=${category}` : ''}`);
    return res.data;
  },
  getShafts: async (category = '') => {
    const res = await api.get(`/inventory/shafts${category ? `?category=${category}` : ''}`);
    return res.data;
  },
  getBalls: async () => {
    const res = await api.get('/inventory/balls');
    return res.data;
  }
};

// LinksID API — resolves golfer passport from Firebase token
const LINKSID_API = import.meta.env.VITE_LINKSID_API || 'https://linksid.colewinds-dev.com';

export const LinksIDService = {
  /**
   * Get or create the user's LinksID passport.
   * Call after Firebase auth succeeds.
   * @param {string} firebaseToken - Firebase ID token
   * @returns {Promise<object>} LinksID passport
   */
  getPassport: async (firebaseToken) => {
    const res = await axios.get(`${LINKSID_API}/passport/me`, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      timeout: 10000,
    });
    return res.data;
  },

  /**
   * Update passport profile fields (swing profile, handicap, etc.)
   * @param {string} firebaseToken
   * @param {object} updates
   */
  updatePassport: async (firebaseToken, updates) => {
    const res = await axios.patch(`${LINKSID_API}/passport/me`, updates, {
      headers: {
        Authorization: `Bearer ${firebaseToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });
    return res.data;
  },

  /**
   * Get bag from LinksID (source of truth for authenticated users).
   * @param {string} firebaseToken
   * @returns {Promise<Array>} Array of bag slot objects
   */
  getBag: async (firebaseToken) => {
    const res = await axios.get(`${LINKSID_API}/bag/?active_only=true`, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      timeout: 10000,
    });
    return res.data;
  },

  /**
   * Get shots for a specific LinksID session.
   * @param {string} firebaseToken
   * @param {string} sessionId
   * @returns {Promise<Array>} Array of shot objects
   */
  getSessionShots: async (firebaseToken, sessionId) => {
    const res = await axios.get(`${LINKSID_API}/sessions/${sessionId}/shots`, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      timeout: 10000,
    });
    return res.data;
  },

  /**
   * Get sessions from LinksID (source of truth for authenticated users).
   * @param {string} firebaseToken
   * @param {number} limit
   * @returns {Promise<Array>} Array of session objects
   */
  getSessions: async (firebaseToken, limit = 50) => {
    const res = await axios.get(`${LINKSID_API}/sessions/?limit=${limit}`, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      timeout: 10000,
    });
    return res.data;
  },

  deleteSession: async (firebaseToken, sessionId) => {
    const res = await axios.delete(`${LINKSID_API}/sessions/${sessionId}`, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      timeout: 10000,
    });
    return res.data;
  },

  /**
   * Bulk import bag from PocketFitter format into LinksID.
   * @param {string} firebaseToken
   * @param {Array} clubs - PocketFitter club objects
   * @returns {Promise<object>}
   */
  importBag: async (firebaseToken, clubs) => {
    const res = await axios.post(`${LINKSID_API}/bag/import`, { clubs }, {
      headers: {
        Authorization: `Bearer ${firebaseToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });
    return res.data;
  },

  /**
   * Get equipment enrichment data from LinksID xref table (public, no auth).
   * @param {string} xrefId - The equipment_xref_id
   * @returns {Promise<object|null>} Enrichment data or null if not found
   */
  /**
   * Delete a club from the LinksID bag.
   * @param {string} firebaseToken
   * @param {string} slotId - The bag slot ID
   */
  deleteClub: async (firebaseToken, slotId) => {
    const res = await axios.delete(`${LINKSID_API}/bag/${slotId}`, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
      timeout: 10000,
    });
    return res.data;
  },

  /**
   * Add a single club to the LinksID bag.
   * @param {string} firebaseToken
   * @param {object} club - { slot, head_manufacturer, head_model, head_loft_deg, ... }
   */
  addClub: async (firebaseToken, club) => {
    const res = await axios.post(`${LINKSID_API}/bag/`, club, {
      headers: {
        Authorization: `Bearer ${firebaseToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });
    return res.data;
  },

  /**
   * Update a club in the LinksID bag.
   * @param {string} firebaseToken
   * @param {string} slotId - The bag slot ID
   * @param {object} updates - Fields to update (head_loft_adjusted_deg, head_lie_deg, etc.)
   */
  updateClub: async (firebaseToken, slotId, updates) => {
    const res = await axios.patch(`${LINKSID_API}/bag/${slotId}`, updates, {
      headers: {
        Authorization: `Bearer ${firebaseToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });
    return res.data;
  },

  getEquipmentInfo: async (xrefId) => {
    try {
      const res = await axios.get(`${LINKSID_API}/equipment/${xrefId}`);
      return res.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

export default api;
